import { useContext } from "react";
import { Dataset, DatasetFieldLabels, DatasetFilters } from "../../api/Datasets";
import { DefinedPropertyObject, IEntityMinimalModel } from "../../api/GenericTypes";
import { SessionContext } from "../../common/contexts/SessionContext";
import { SelectFormField } from "../../common/formfields/SelectFormField";
import { ToggleFormField } from "../../common/formfields/ToggleFormField";
import {
  TabStoreFilterSidebarProps,
  TabStoreFilterFormProvider,
} from "../../common/sidebar/filtersidebar/TabStoreFilterFormProvider";
import { ProjectsVirtualizedSelectForm } from "../../common/forms/EntityForms/formsVirtualized/ProjectsVirtualizedSelectForm";
import { DataFormatsVirtualizedSelectForm } from "../../common/forms/EntityForms/formsVirtualized/DataFormatsVirtualizedSelectForm";
import { EntityFilterSideBar } from "../../common/sidebar/filtersidebar/EntityFilterBar";
import { SplitDatePickerFormField } from "../../common/formfields/DatePickerFormField/SplitDatePickerFormField";
import { BridgesVirtualizedSelectForm } from "../../common/forms/EntityForms/formsVirtualized/BridgeVirtualizedSelectForm";
import { DataSourcesVirtualizedSelectForm } from "../../common/forms/EntityForms/formsVirtualized/DataSourcesVirtualizedSelectForm";
import { CustomTypesVirtualizedSelectForm } from "../../common/forms/EntityForms/formsVirtualized/CustomTypesVirtualizedSelectForm";
import {
  CustomFieldFilterLayout,
  CustomFieldFilterLayoutForm,
} from "../../Customization/CustomFieldFilters/CustomFieldFilterLayout/CustomFieldFilterLayout";
import {
  CombinedModificationRecordEntityDefaultFormValues,
  combinedModificationRecordEntityFiltersToForm,
  combinedModificationRecordEntityFormToFilters,
  ICombinedModificationRecordEntityForm,
} from "../../common/sidebar/filtersidebar/GenericFilters/CombinedModificationRecordEntityFilterFormModels";
import {
  ILockableEntityForm,
  LockableEntityDefaultFilterFormValues,
  lockableEntityFiltersToForm,
  lockableEntityFormToFilters,
} from "../../common/sidebar/filtersidebar/GenericFilters/LockableEntityFilterFormModels";
import {
  ISignableEntityForm,
  SignableEntityDefaultFilterFormValues,
  signableEntityFiltersToForm,
  signableEntityFormToFilters,
} from "../../common/sidebar/filtersidebar/GenericFilters/SignableEntityFilterFormModels";
import {
  ISoftDeletableEntityForm,
  SoftDeletableEntityDefaultFilterFormValues,
  softDeletableEntityFiltersToForm,
  softDeletableEntityFormToFilters,
} from "../../common/sidebar/filtersidebar/GenericFilters/SoftDeletableEntityFilterFormModels";
import { CombinedModificationRecordEntityFilterForm } from "../../common/sidebar/filtersidebar/GenericFilters/CombinedModificationRecordEntityFilterForm";
import { SignableEntityFilterForm } from "../../common/sidebar/filtersidebar/GenericFilters/SignableEntityFilterForm";
import { GenericVirtualizedSelectFormField } from "../../common/formfields/GenericVirtualizedSelectFormField/GenericVirtualizedSelectFormField";
import { toValidDate } from "../../common/datetime/DateTimeFormatter";
import { PersonsVirtualizedSelectForm } from "../../common/forms/EntityForms/formsVirtualized/PersonsVirtualizedSelectForm";
import { defaultDatasetsFilters } from "./DatasetsTable";
import { FoldableFilters } from "../../common/sidebar/filtersidebar/GenericFilters/common/FoldableFilters";

export interface DatasetsFilterForm
  extends ICombinedModificationRecordEntityForm,
    ILockableEntityForm,
    ISignableEntityForm,
    ISoftDeletableEntityForm,
    CustomFieldFilterLayoutForm {
  project?: IEntityMinimalModel[] | undefined | null;
  ownerIds?: IEntityMinimalModel[] | undefined | null;
  dataSourceIds?: IEntityMinimalModel[] | undefined | null;
  bridgeIds?: IEntityMinimalModel[] | undefined | null;
  creationDateFrom?: Date | undefined | null;
  creationDateTo?: Date | undefined | null;
  parsingState?: { value: any; label: any }[] | undefined | null;
  customType?: { id: number; name: string }[] | undefined | null;
  includeUnclaimed?: boolean | undefined | null;
  includeViewableEntities?: boolean | undefined | null;
  formats?: IEntityMinimalModel<string>[] | undefined | null;
  claimed?: IEntityMinimalModel<string> | undefined | null;
}

export const datasetDefaultValues: DefinedPropertyObject<DatasetsFilterForm> = {
  project: null,
  ownerIds: null,
  dataSourceIds: null,
  bridgeIds: null,
  creationDateFrom: null,
  creationDateTo: null,
  parsingState: null,
  customType: null,
  includeUnclaimed: true,
  includeViewableEntities: false,
  formats: null,
  claimed: { id: "all", name: "All" },
  ...CombinedModificationRecordEntityDefaultFormValues,
  ...LockableEntityDefaultFilterFormValues,
  ...SignableEntityDefaultFilterFormValues,
  ...SoftDeletableEntityDefaultFilterFormValues,
  customFieldFilters: [],
};

export const datasetsFormToFilters = (form: DatasetsFilterForm): DatasetFilters => {
  let filters: DatasetFilters = {
    ...combinedModificationRecordEntityFormToFilters(form),
    ...lockableEntityFormToFilters(form),
    ...signableEntityFormToFilters(form),
    ...softDeletableEntityFormToFilters(form),
  };

  filters.customTypeIds =
    Array.isArray(form.customType) && form.customType.length > 0 ? form.customType.map((d) => d.id) : null;
  filters.projectIds = Array.isArray(form.project) && form.project.length > 0 ? form.project?.map((d) => d.id) : null;
  filters.ownerIds = Array.isArray(form.ownerIds) && form.ownerIds.length > 0 ? form.ownerIds?.map((d) => d.id) : null;
  filters.dataSourceIds =
    Array.isArray(form.dataSourceIds) && form.dataSourceIds.length > 0 ? form.dataSourceIds?.map((d) => d.id) : null;
  filters.bridgeIds =
    Array.isArray(form.bridgeIds) && form.bridgeIds.length > 0 ? form.bridgeIds?.map((d) => d.id) : null;
  filters.creationDateFrom = toValidDate(form.creationDateFrom);
  filters.creationDateTo = toValidDate(form.creationDateTo);
  filters.parsingState =
    Array.isArray(form.parsingState) && form.parsingState.length > 0 ? form.parsingState?.map((d) => d.value) : null;
  filters.formatIds = Array.isArray(form.formats) && form.formats.length > 0 ? form.formats?.map((d) => d.id) : null;
  filters.isClaimed =
    (form.claimed ? (form.claimed.id === "claimed" ? true : form.claimed.id === "unclaimed" ? false : null) : null) ??
    null;
  filters.includeUnclaimed = (form.claimed && (form.claimed.id === "all" || form.claimed.id === "unclaimed")) || false;
  filters.includeViewableEntities = form.includeViewableEntities || false;
  return filters;
};

/**
 * Converts DatasetFilters to DatasetsFilterForm
 * @param filters DatasetFilters
 * @returns DatasetsFilterForm
 * @author @CorradoSurmanowicz
 */
export const datasetsFiltersToForm = (filters: DatasetFilters): DatasetsFilterForm => {
  return {
    project: filters.projectIds?.map((id) => ({ id, name: "" })) || null,
    dataSourceIds: filters.dataSourceIds?.map((id) => ({ id, name: "" })) || null,
    bridgeIds: filters.bridgeIds?.map((id) => ({ id, name: "" })) || null,
    creationDateFrom: filters.creationDateFrom ? new Date(filters.creationDateFrom) : null,
    creationDateTo: filters.creationDateTo ? new Date(filters.creationDateTo) : null,
    parsingState: filters.parsingState?.map((value) => ({ value, label: value })) || null,
    customType: filters.customTypeIds?.map((id) => ({ id, name: "" })) || null,
    includeUnclaimed: filters.includeUnclaimed || false,
    includeViewableEntities: filters.includeViewableEntities || false,
    formats: filters.formatIds?.map((id) => ({ id, name: "" })) || null,
    claimed: !filters.isClaimed
      ? { id: "all", name: "All" }
      : filters.isClaimed
      ? { id: "claimed", name: "Claimed" }
      : { id: "unclaimed", name: "Unclaimed" },
    ...combinedModificationRecordEntityFiltersToForm(filters),
    ...lockableEntityFiltersToForm(filters),
    ...signableEntityFiltersToForm(filters),
    ...softDeletableEntityFiltersToForm(filters),
  };
};

export const DatasetsFilterbar = ({
  initialValues,
  onFormChange,
  dispatchTabStore,
  tabsLoading,
  currentTab,
}: TabStoreFilterSidebarProps<Dataset, DatasetsFilterForm, DatasetFilters>) => {
  const { session } = useContext(SessionContext);

  // const stateArray = [
  //   !!initialValues?.customType,
  //   !!initialValues?.creationDateFrom,
  //   !!initialValues?.creationDateTo,
  //   !!initialValues?.project?.length,
  //   !!initialValues?.ownerIds?.length,
  //   !!initialValues?.bridgeIds?.length,
  //   !!initialValues?.dataSourceIds?.length,
  //   !!initialValues?.parsingState?.length,
  //   !!initialValues?.formats?.length,
  //   initialValues?.includeUnclaimed !== true,
  //   initialValues?.claimed ? initialValues.claimed?.id !== "all" : false,
  // ];

  return (
    <TabStoreFilterFormProvider
      tabsLoading={tabsLoading}
      initialValues={initialValues}
      defaultValues={datasetDefaultValues}
      defaultFilters={defaultDatasetsFilters}
      formToFilters={datasetsFormToFilters}
      onFormChange={onFormChange}
      dispatchTabStore={dispatchTabStore}
      customTypeConstraint="Dataset"
    >
      {({ control, register, errors, onReset, customFieldsDict }) => (
        <EntityFilterSideBar onReset={onReset}>
          <FoldableFilters
            label="General"
            // defaultOpen={stateArray.some((p) => p) && !!stateArray.length}
            defaultOpen
          >
            <CustomTypesVirtualizedSelectForm
              id="customType"
              label="Dataset types"
              placeholder="Select dataset types..."
              filters={{ entityTypes: ["Dataset"] }}
              control={control}
              showControls={false}
              isMulti
              enableBasicOption
              hideIfNoTypes
            />

            <SplitDatePickerFormField
              control={control}
              idStart="creationDateFrom"
              idEnd="creationDateTo"
              label={`${DatasetFieldLabels.creationDate} (from - to)`}
              horizontal={false}
              showTimeSelect
            />

            <ProjectsVirtualizedSelectForm id="project" control={control} showControls={false} isMulti />

            {currentTab !== "unclaimed" && currentTab !== "mydata" && (
              <PersonsVirtualizedSelectForm id="ownerIds" label="Owned by" control={control} isMulti />
            )}

            <BridgesVirtualizedSelectForm
              id="bridgeIds"
              label="Bridge"
              control={control}
              showControls={false}
              isMulti
            />

            <DataSourcesVirtualizedSelectForm
              id="dataSourceIds"
              label="Data source"
              control={control}
              showControls={false}
              isMulti
            />

            <DataFormatsVirtualizedSelectForm id={"formats"} control={control} showControls={false} isMulti />

            {session?.features.development_mode && (
              <SelectFormField
                id={"parsingState"}
                label={"State"}
                {...register}
                getOptionLabel={(opt: { label: any; value: any }) => opt.label}
                getOptionValue={(opt: { label: any; value: any }) => opt.value}
                items={[
                  {
                    label: "Not yet parsed",
                    value: "NotYetParsed",
                  },
                  {
                    label: "Not parsable",
                    value: "NotParsable",
                  },
                  {
                    label: "Parsing failed",
                    value: "ParsingFailed",
                  },
                  {
                    label: "Parsed successfully",
                    value: "ParsedSuccessfully",
                  },
                ]}
                control={control}
                horizontal={false}
                isMulti={true}
              />
            )}

            {currentTab !== "unclaimed" && (
              <GenericVirtualizedSelectFormField
                id={"claimed"}
                label={"Claim status"}
                control={control}
                items={[
                  { id: "claimed", name: "Claimed" },
                  { id: "unclaimed", name: "Unclaimed" },
                  { id: "all", name: "All" },
                ]}
                disabled={currentTab === "unclaimed"}
                isMulti={false}
              />
            )}

            {session?.features.development_mode && (
              <ToggleFormField
                id={"includeViewableEntities"}
                control={control}
                label={"Include viewable entities"}
                horizontal
              />
            )}
          </FoldableFilters>

          {customFieldsDict && (
            <CustomFieldFilterLayout
              control={control}
              errors={errors}
              customFieldsDict={customFieldsDict}
              entityTypes={["Dataset"]}
            />
          )}

          <CombinedModificationRecordEntityFilterForm
            control={control}
            currentTab={currentTab}
            initialValues={initialValues}
          />

          <SignableEntityFilterForm control={control} initialValues={initialValues} />

          {/* <SoftDeletableEntityFilterForm control={control} currentTab={currentTab} initialValues={initialValues} /> */}
        </EntityFilterSideBar>
      )}
    </TabStoreFilterFormProvider>
  );
};
